// client/routing-state.ts
import { BehaviorSubject, combineLatest, fromEvent, map, startWith } from "rxjs";
import { routerConfig } from "../config.js";
import { ReactiveSearchParams } from "./reactive-search-params.js";
var NO_CHANGE = Symbol("URL_SEARCH_PARAMS_NO_CHANGE");
var cleanPath = (path) => {
  path = decodeURI(path);
  return path.slice(-1)[0] === "/" && path.length > 1 ? path.slice(0, -1) : path;
};
var cleanSearchParams = (search) => new URLSearchParams(search);
var cleanHash = (hash) => decodeURI(hash.startsWith("#") ? hash.substring(1) : hash);
var ReactiveRoutingState = class _ReactiveRoutingState extends BehaviorSubject {
  constructor() {
    const options = routerConfig.get("USE_VIRTUAL_ROUTING") ? {
      path: routerConfig.get("VIRTUAL_PATH") ?? "/",
      searchParams: new URLSearchParams(routerConfig.get("VIRTUAL_SEARCH_PARAMS") ?? ""),
      hash: routerConfig.get("VIRTUAL_HASH") ?? ""
    } : {
      path: window.location.pathname,
      searchParams: new URLSearchParams(window.location.search),
      hash: window.location.hash
    };
    super(options);
    this._reactiveSearchParams = new ReactiveSearchParams(
      routerConfig.get("USE_VIRTUAL_ROUTING") ? new URLSearchParams(routerConfig.get("VIRTUAL_SEARCH_PARAMS") ?? "") : void 0
    );
    if (!routerConfig.get("USE_VIRTUAL_ROUTING")) {
      combineLatest([
        fromEvent(window, "hashchange").pipe(startWith(null)),
        fromEvent(window, "location-changed").pipe(startWith(null)),
        fromEvent(window, "popstate").pipe(startWith(null))
      ]).subscribe(
        () => this._next({
          path: cleanPath(window.location.pathname),
          searchParams: cleanSearchParams(window.location.search),
          hash: cleanHash(window.location.hash)
        })
      );
    }
    this._reactiveSearchParams.subscribe((searchParams) => {
      if (ReactiveSearchParams.isEqualTo(searchParams, this.getValue().searchParams))
        return;
      this.next({
        path: NO_CHANGE,
        searchParams: new URLSearchParams(searchParams.toString()),
        hash: NO_CHANGE
      });
    });
  }
  static get instance() {
    if (!this._instance)
      this._instance = new _ReactiveRoutingState();
    return this._instance;
  }
  /**
   * path observable, fires every time path is changed through calling `next` or browser url changes
   */
  get path$() {
    return this.asObservable().pipe(map((x) => x.path));
  }
  /**
   * hash observable, fires every time hash is changed through calling `next` or browser url changes
   */
  get hash$() {
    return this.asObservable().pipe(map((x) => x.hash));
  }
  get path() {
    return this.getValue().path;
  }
  set path(path) {
    this.next({ path, searchParams: this.getValue().searchParams, hash: this.getValue().hash });
  }
  get searchParams() {
    return this._reactiveSearchParams;
  }
  set searchParams(value) {
    throw new Error(
      "Dont use searchParams getter, use router.searchParams.next() to set new searchParams or router.searchParams.set(key, value) to only set one search param key"
    );
  }
  get hash() {
    return this.getValue().hash;
  }
  set hash(hash) {
    this.next({ path: this.getValue().path, searchParams: this.getValue().searchParams, hash });
  }
  getValue() {
    const sup = super.getValue();
    const self = this;
    return {
      get path() {
        return sup.path;
      },
      get searchParams() {
        return sup.searchParams;
      },
      get hash() {
        return sup.hash;
      },
      set path(path) {
        if (path.startsWith("http"))
          throw new Error("use global goTo helper instead of routingState");
        self.path = path;
      },
      set searchParams(searchParams) {
        self.searchParams.next(searchParams);
      },
      set hash(hash) {
        self.hash = hash;
      }
    };
  }
  _next(routingTarget) {
    const next = generateFullRoutingStateFromInput(routingTarget);
    if (next.path === this.path && ReactiveSearchParams.isEqualTo(next.searchParams, this.getValue().searchParams) && next.hash === this.hash)
      return;
    console.log(
      "%c  routing state :: " + next.path + " // use virtual path: " + (routerConfig.get("USE_VIRTUAL_ROUTING") ? "true" : "false"),
      "color: #bada55"
    );
    super.next(next);
    this._reactiveSearchParams.next(new URLSearchParams(this.getValue().searchParams.toString()));
  }
  /**
   * trigger route change that will be reflected to browser (if not in virtual mode)
   */
  next(routingTarget) {
    this._next(routingTarget);
    if (!routerConfig.get("USE_VIRTUAL_ROUTING"))
      this.reflectStateToBrowser(routingTarget.replaceState);
  }
  reflectStateToBrowser(replaceState = false) {
    if (routerConfig.get("USE_VIRTUAL_ROUTING"))
      throw new Error("dont reflect state to browser when using virtual routing");
    const newhref = generateHrefFromRoutingState({
      path: this.path,
      searchParams: this.getValue().searchParams,
      hash: this.hash
    });
    if (replaceState)
      window.history.replaceState({ path: newhref }, "", newhref);
    else
      window.history.pushState({ path: newhref }, "", newhref);
    window.dispatchEvent(new CustomEvent("location-changed", { node: window }));
  }
};
var routingState = ReactiveRoutingState.instance;
function generateHrefFromRoutingState(routingOptions) {
  const urlQueryParams = routingOptions.searchParams.size ? `?${routingOptions.searchParams.toString()}` : "";
  const urlHash = routingOptions.hash ? `#${routingOptions.hash}` : "";
  return `${encodeURI(routingOptions.path)}${urlQueryParams}${urlHash}`;
}
function generateFullRoutingStateFromInput(routingTarget) {
  return {
    path: routingTarget.path && routingTarget.path !== NO_CHANGE ? cleanPath(routingTarget.path) : routingState.getValue().path,
    searchParams: routingTarget.searchParams ? routingTarget.searchParams === NO_CHANGE ? routingState.getValue().searchParams : cleanSearchParams(routingTarget.searchParams) : new URLSearchParams(),
    hash: routingTarget.hash ? routingTarget.hash === NO_CHANGE ? routingState.getValue().hash : cleanHash(routingTarget.hash) : ""
  };
}
function joinPathParts(url1, url2) {
  return `${url1}${url1.endsWith("/") ? "" : "/"}${url2.startsWith("/") ? url2.slice(1) : url2}`;
}
export {
  NO_CHANGE,
  generateFullRoutingStateFromInput,
  generateHrefFromRoutingState,
  joinPathParts,
  routingState
};
